import {
  Button,
  Input,
  SearchDropdownMultiSelect,
  SingleSelect,
  Search,
  TrashAlt,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Eye,
  EyeOff,
  SpinnerLoader,
  Toastify,
} from 'lift-ui';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { AlertFormData, BrandConfig } from '../types';
import { parseCronExpression } from '../utils/cronParser';
import { dmgAlertApiService } from '../utils/dmgAlertApiService';
import {
  convertHtmlToSlackMarkdown,
  convertHtmlToEmailHtml,
  convertHtmlToPlainText,
} from '../utils/helpers';
import { buildThresholdConfigs } from '../utils/mappers';

import { SlackMessageRenderer } from './SlackMessageRenderer';
import '../../../styles/dmg-alert-system.styles.css';

interface AlertSummaryStepProps {
  formData: AlertFormData;
  selectedBrands: BrandConfig[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onScheduleChange?: any; // Function type: (key: 'cadence' | 'days' | 'day' | 'time' | 'timezone', value: string | number | number[]) => void
  // eslint-disable-next-line no-unused-vars
  onMessageTemplateChange?: (channel: 'slack' | 'email' | 'web', content: string) => void;
  // eslint-disable-next-line no-unused-vars
  onRemoveThresholdRow?: (brandId: number, thresholdIndex: number) => void;
  readOnly?: boolean;
  selectedCommunicationMethods?: string[];
  userId?: number;
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
    default_threshold_config?: Array<{
      value: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      max_value?: string | number;
      period_days?: number;
    }>;
    alert_query_config?: {
      properties: string[];
    };
  }>;
}

export const AlertSummaryStep: React.FC<AlertSummaryStepProps> = ({
  formData,
  selectedBrands,
  onScheduleChange,
  onMessageTemplateChange,
  onRemoveThresholdRow,
  readOnly = false,
  selectedCommunicationMethods = [],
  userId,
  alertTypes = [],
}) => {
  // Determine alert type
  const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
  const isPacingTool = selectedAlertType?.name === 'pacing';
  const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';

  // Map operator values for display (pacing operators: + -> >, - -> <, +- -> ><, += -> >=, -= -> <=)
  const getDisplayOperator = (operator: string): string => {
    if (isPacingTool) {
      const operatorMap: Record<string, string> = {
        '+': '>',
        '-': '<',
        '+-': '><',
        '+=': '>=',
        '-=': '<=',
        '+-=': '>=<=',
      };
      return operatorMap[operator] || operator;
    }
    return operator;
  };

  // Helper function to convert number to ordinal (1st, 2nd, 3rd, etc.)
  const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Display labels for relative comparison date options (aligned with backend ComparisionDate enum)
  const RELATIVE_DATE_LABELS: Record<string, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This Week',
    this_month: 'This Month',
    this_quarter: 'This Quarter',
    this_year: 'This Year',
    last_week: 'Last Week',
    last_month: 'Last Month',
    last_quarter: 'Last Quarter',
    last_year: 'Last Year',
    last_2_days: 'Last 2 Days',
    last_3_days: 'Last 3 Days',
    last_4_days: 'Last 4 Days',
    last_7_days: 'Last 7 Days',
    last_9_days: 'Last 9 Days',
    last_14_days: 'Last 14 Days',
    last_28_days: 'Last 28 Days',
    last_30_days: 'Last 30 Days',
    last_60_days: 'Last 60 Days',
    last_90_days: 'Last 90 Days',
    last_180_days: 'Last 180 Days',
    last_365_days: 'Last 365 Days',
    last_730_days: 'Last 730 Days',
    latest_4_quarters: 'Latest 4 Quarters',
  };

  const getRelativeDateRangeLabel = (optionId: string | undefined): string | null => {
    if (!optionId || typeof optionId !== 'string') return null;
    const label = RELATIVE_DATE_LABELS[optionId.toLowerCase()];
    if (label) return label;
    // Fallback: title-case the id (e.g. "last_7_days" -> "Last 7 Days")
    return optionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format date range from endDate (format: "startISO|endISO")
  const formatDateRange = (
    endDateStr: string | undefined
  ): { start: string; end: string } | null => {
    if (!endDateStr || !endDateStr.includes('|')) return null;
    try {
      const [startISO, endISO] = endDateStr.split('|');
      const startDate = new Date(startISO);
      const endDate = new Date(endISO);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
      const formatDate = (date: Date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };
      return { start: formatDate(startDate), end: formatDate(endDate) };
    } catch {
      return null;
    }
  };

  // Helper function to format threshold value for taxonomy alerts with updated_at value type
  const formatThresholdValue = (
    threshold: string | number | undefined,
    taxonomyValueType: string | undefined
  ): string => {
    // If it's a taxonomy alert with updated_at value type, format the date
    if (taxonomyValueType === 'updated_at' && threshold) {
      try {
        // Try to parse as ISO date string
        const date = new Date(threshold.toString());
        if (!isNaN(date.getTime())) {
          // Format as: "Jan 8, 2026, 3:19 PM"
          const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          };
          return date.toLocaleString('en-US', options);
        }
      } catch {
        // If parsing fails, return as is
      }
    }
    // Return threshold as string for other cases
    return threshold?.toString() || '';
  };

  // Helper function to format dimension display from dimensionValue JSON
  // dimensionValue contains array like: [{"name": "Google", "type": "Platform"}, ...]
  // We format it as: "Platform: Google | Tactic: Brand"
  const formatDimensionDisplay = (
    dimension: string | undefined,
    dimensionValue: string | undefined
  ): string[] => {
    // First try to parse dimensionValue which contains the actual dimension data
    if (dimensionValue) {
      try {
        const parsed = JSON.parse(dimensionValue);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Check for "All Dimensions" case
          if (parsed.length === 1 && parsed[0].name === 'All' && parsed[0].type === 'All') {
            return ['All Dimensions'];
          }
          // Group by type and format as "type: name1, name2"
          const typeGroups: Record<string, string[]> = {};
          parsed.forEach((dim: { name: string; type: string }) => {
            if (dim.type && dim.name && dim.type !== 'All') {
              if (!typeGroups[dim.type]) {
                typeGroups[dim.type] = [];
              }
              typeGroups[dim.type].push(dim.name);
            }
          });
          const parts = Object.entries(typeGroups).map(
            ([type, names]) => `${type}: ${names.join(', ')}`
          );
          return parts.length > 0 ? parts : ['All Dimensions'];
        }
      } catch {
        // Not valid JSON, fall through
      }
    }
    // Fallback to dimension field if it looks like a display name (contains ":")
    if (dimension && dimension.includes(':')) {
      return dimension.split('|').map(part => part.trim());
    }
    // If dimension is just a number (sheet config ID), show placeholder
    if (dimension && /^\d+$/.test(dimension)) {
      return ['Dimension Selected'];
    }
    return dimension ? [dimension] : [];
  };

  // Filter channels based on selected communication methods from step 3
  // Map: '1' = Slack, '2' = Email, '3' or '4' = Lift Notification (web)
  const allChannels = [
    { key: 'Slack' as const, value: 'slack' as const, methodId: '1' },
    { key: 'Email' as const, value: 'email' as const, methodId: '2' },
    { key: 'Lift Notification' as const, value: 'web' as const, methodIds: ['3', '4'] },
  ];

  const visibleChannels = allChannels.filter(channel => {
    if (channel.methodId) {
      // Single method ID (Slack or Email)
      return selectedCommunicationMethods.includes(channel.methodId);
    } else if (channel.methodIds) {
      // Multiple method IDs (Lift Notification can be '3' or '4')
      return channel.methodIds.some(id => selectedCommunicationMethods.includes(id));
    }
    return false;
  });

  // Only show channels that are selected in step 3
  const channelsToShow = visibleChannels;

  // Determine initial selected channel - use first visible channel if current is not available
  const getInitialChannel = (): 'Slack' | 'Email' | 'Lift Notification' => {
    const currentChannel = formData.notificationChannel || 'Slack';
    const isCurrentVisible = channelsToShow.some(ch => ch.key === currentChannel);
    if (isCurrentVisible) {
      return currentChannel as 'Slack' | 'Email' | 'Lift Notification';
    }
    return channelsToShow.length > 0
      ? (channelsToShow[0].key as 'Slack' | 'Email' | 'Lift Notification')
      : 'Slack';
  };

  const [selectedChannel, setSelectedChannel] = useState<'Slack' | 'Email' | 'Lift Notification'>(
    getInitialChannel()
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrandFilters, setSelectedBrandFilters] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const quillRef = useRef<ReactQuill>(null);
  const [pendingDeletion, setPendingDeletion] = useState<{
    brandId: number;
    thresholdIndex: number;
    brandName: string;
    pixelName?: string;
    eventName?: string;
    dimension?: string;
    metric?: string;
    startDate?: string;
    endDate?: string;
    calendarType?: string;
    comparisonDate?: string;
    taxonomyType?: string;
    taxonomyValueType?: string;
  } | null>(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<
    Array<{
      brand_id: number;
      brand_name: string;
      email?: string;
      slack?: string;
      web?: string;
      email_subject?: string;
      slack_table_block?: {
        type: string;
        rows: Array<
          Array<{
            type: string;
            text: string;
          }>
        >;
        column_settings: Array<{
          align: string;
          is_wrapped: boolean;
        }>;
      };
    }>
  >([]);

  // Update selected channel if it's not in the visible channels list
  useEffect(() => {
    const isCurrentChannelVisible = channelsToShow.some(ch => ch.key === selectedChannel);
    if (!isCurrentChannelVisible && channelsToShow.length > 0) {
      setSelectedChannel(channelsToShow[0].key as 'Slack' | 'Email' | 'Lift Notification');
    }
  }, [selectedCommunicationMethods, channelsToShow, selectedChannel]);
  const [parsedSchedule, setParsedSchedule] = useState<{
    cadence: string;
    time: string;
    days?: number[];
    day?: number[];
  } | null>(null);

  // Parse cron_info on component mount or when formData changes
  useEffect(() => {
    // @ts-ignore - cron_info might exist on formData
    const cronInfo = formData.cron_info;

    if (cronInfo && typeof cronInfo === 'string') {
      const parsed = parseCronExpression(cronInfo);

      if (parsed) {
        setParsedSchedule(parsed);

        // Update formData with parsed values
        if (onScheduleChange) {
          onScheduleChange('cadence', parsed.cadence);
          if (parsed.time) {
            onScheduleChange('time', parsed.time);
          }
          if (parsed.days) {
            onScheduleChange('days', parsed.days);
          }
          if (parsed.day) {
            onScheduleChange('day', parsed.day);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cron_info]);

  // Compute cadence value
  // If formData.cadence is manually set (not 'realtime'), use it (user's selection takes precedence)
  // Otherwise, use parsedSchedule from cron_info (source of truth for initial load)
  const cadenceValue =
    formData.cadence && ['daily', 'weekly', 'monthly'].includes(formData.cadence)
      ? formData.cadence
      : parsedSchedule?.cadence || 'daily';

  // Quill modules configuration
  const modulesCustom = useMemo(
    () =>
      readOnly
        ? { toolbar: false }
        : {
            toolbar: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['clean'],
            ],
          },
    [readOnly]
  );

  // Quill formats configuration
  const formatsCustom = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet'];

  // Flatten all thresholds with their brand info for filtering
  const allThresholds = useMemo(() => {
    return selectedBrands.flatMap(brand =>
      brand.thresholds.map((threshold, thresholdIndex) => ({
        ...threshold,
        brandName: brand.brandName,
        brandId: brand.brandId,
        thresholdIndex, // Store the index within the brand's thresholds array
      }))
    );
  }, [selectedBrands]);

  // Filter thresholds based on search and filters
  const filteredThresholds = useMemo(() => {
    let filtered = allThresholds;

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.brandName.toLowerCase().includes(searchLower) ||
          (item.pixelName || '').toLowerCase().includes(searchLower) ||
          (item.eventName || '').toLowerCase().includes(searchLower) ||
          (item.dimension || '').toLowerCase().includes(searchLower) ||
          (item.metric || '').toLowerCase().includes(searchLower) ||
          `${item.operator} ${item.threshold}`.toLowerCase().includes(searchLower)
      );
    }

    // Apply brand filter
    if (selectedBrandFilters.length > 0) {
      filtered = filtered.filter(item => selectedBrandFilters.includes(item.brandName));
    }

    return filtered;
  }, [allThresholds, searchTerm, selectedBrandFilters]);

  // Paginate filtered results
  const paginatedThresholds = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredThresholds.slice(startIndex, endIndex);
  }, [filteredThresholds, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredThresholds.length / pageSize);

  // Get unique brands for filter dropdown
  const uniqueBrands = useMemo(() => {
    return Array.from(new Set(selectedBrands.map(b => b.brandName))).sort();
  }, [selectedBrands]);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBrandFilters]);

  // Set default cadence to 'daily' if not set or if it's 'realtime' (run on mount)
  // But only if we don't have a cron_info to parse
  React.useEffect(() => {
    // @ts-ignore
    const hasCronInfo = formData.cron_info && typeof formData.cron_info === 'string';

    if (
      (!formData.cadence || formData.cadence === 'realtime') &&
      onScheduleChange &&
      !hasCronInfo
    ) {
      onScheduleChange('cadence', 'daily');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentMessageTemplate = () => {
    const channelKey =
      selectedChannel === 'Slack' ? 'slack' : selectedChannel === 'Email' ? 'email' : 'web';
    return formData.messageTemplates?.[channelKey] || '';
  };

  const handleEditorChange = (content: string) => {
    const channelKey =
      selectedChannel === 'Slack' ? 'slack' : selectedChannel === 'Email' ? 'email' : 'web';
    // Update the message template for the current channel
    if (onMessageTemplateChange) {
      onMessageTemplateChange(channelKey, content);
    }
  };

  const handleClearFilters = () => {
    setSelectedBrandFilters([]);
    setSearchTerm('');
  };

  const hasActiveFilters = selectedBrandFilters.length > 0 || searchTerm;

  // Handle preview toggle
  const handleTogglePreview = useCallback(async () => {
    // If preview is currently shown, just hide it
    if (showPreview) {
      setShowPreview(false);
      return;
    }

    // Otherwise, fetch the preview
    const alertTypeId = formData.alertTypeId;
    if (!alertTypeId) {
      Toastify('error', 'Alert type is required to preview template');
      return;
    }

    if (!userId) {
      Toastify('error', 'User ID is required to preview template');
      return;
    }

    // Get brand IDs
    const selectedBrandIds =
      selectedBrands
        ?.map(brand => brand.brandId)
        .filter((id): id is number => typeof id === 'number') || [];

    if (selectedBrandIds.length === 0) {
      Toastify('error', 'At least one brand is required to preview template');
      return;
    }

    // Determine which channels are selected
    const isSlackSelected = selectedCommunicationMethods.includes('1');
    const isEmailSelected = selectedCommunicationMethods.includes('2');
    const isWebSelected = selectedCommunicationMethods.some(id => id === '3' || id === '4');

    // Build message template object
    const messageTemplate: {
      email_template?: string;
      slack_template?: string;
      web_template?: string;
    } = {};

    if (isEmailSelected && formData.messageTemplates?.email) {
      messageTemplate.email_template = convertHtmlToEmailHtml(formData.messageTemplates.email);
    }
    if (isSlackSelected && formData.messageTemplates?.slack) {
      messageTemplate.slack_template = convertHtmlToSlackMarkdown(formData.messageTemplates.slack);
    }
    if (isWebSelected && formData.messageTemplates?.web) {
      messageTemplate.web_template = convertHtmlToEmailHtml(formData.messageTemplates.web);
    }

    if (Object.keys(messageTemplate).length === 0) {
      Toastify('error', 'Please fill in at least one message template to preview');
      return;
    }

    // Build threshold configs for preview
    const thresholdConfigs = buildThresholdConfigs(formData, selectedBrands, selectedBrandIds);

    setIsLoadingPreview(true);
    try {
      const result = await dmgAlertApiService.previewTemplate({
        typeId: alertTypeId,
        brandId: selectedBrandIds[0],
        userId: userId!,
        messageTemplate,
        thresholdConfigs,
        includeTable: true,
        alertName: formData.alertName,
      });

      if (result.success && result.previews.length > 0) {
        setPreviewContent(result.previews);
        setShowPreview(true);
      } else {
        Toastify('error', result.message || 'Failed to load preview');
      }
    } catch (error: unknown) {
      console.error('Error loading preview:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load preview';
      Toastify('error', errorMessage);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [showPreview, formData, selectedBrands, userId, selectedCommunicationMethods]);

  return (
    <div className="space-y-6">
      <div
        className="bg-white rounded-xl p-8 space-y-8 border border-gray-200 shadow-sm"
        style={{ minHeight: 'calc(90vh - 7rem)', width: '100%', maxWidth: '100%' }}
      >
        {/* Title */}
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-xl font-semibold text-gray-900">Alert Summary</h3>
          <p className="text-sm text-gray-500 mt-1">Review and confirm your alert configuration</p>
        </div>

        {/* Summary Fields */}
        <div className="grid grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 block">Alert Name</label>
            <input
              type="text"
              value={formData.alertName || ''}
              disabled
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 font-medium cursor-not-allowed"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 block">Email Id</label>
            <input
              type="text"
              value={formData.emailId || ''}
              disabled
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 block">
              Tool<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={formData.toolName || ''}
              disabled
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 font-medium cursor-not-allowed"
            />
          </div>
        </div>

        {/* Schedule Alert */}
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Schedule Configuration</h4>
          <div className="flex flex-wrap lg:flex-nowrap gap-4 items-end">
            <div style={{ width: '9rem', minWidth: '9rem' }}>
              <SingleSelect
                key={`cadence-${cadenceValue}`}
                label="Every"
                defaultValue={cadenceValue}
                onChange={
                  readOnly
                    ? undefined
                    : value => {
                        onScheduleChange?.('cadence', value);

                        // Set appropriate defaults based on new cadence
                        if (value === 'weekly') {
                          // Default to Monday for weekly
                          onScheduleChange?.('days', [1]);
                        } else if (value === 'monthly') {
                          // Default to day 1 for monthly
                          onScheduleChange?.('day', [1]);
                        } else if (value === 'daily') {
                          // Default to all days for daily
                          onScheduleChange?.('days', [1, 2, 3, 4, 5, 6, 7]);
                        }
                      }
                }
                options={[
                  { label: 'Day', value: 'daily' },
                  { label: 'Week', value: 'weekly' },
                  { label: 'Month', value: 'monthly' },
                ]}
                placeholder="Select cadence"
                className="w-full"
                containerClassName="w-full"
                disabled={readOnly}
              />
            </div>
            {cadenceValue === 'weekly' &&
              (() => {
                // If cadence is manually set (not 'realtime'), use formData.scheduleConfig (user's selection)
                // Otherwise, use parsedSchedule from cron_info (initial load)
                const selectedDays =
                  formData.cadence && ['daily', 'weekly', 'monthly'].includes(formData.cadence)
                    ? formData.scheduleConfig?.days || [1]
                    : parsedSchedule?.days || formData.scheduleConfig?.days || [1];
                const selectedDaysCount = selectedDays.length;
                const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                const sortedDays = [...selectedDays].sort((a, b) => a - b);
                const visibleDays = sortedDays.slice(0, 3);
                const remainingDays = sortedDays.slice(3);
                const remainingCount = remainingDays.length;
                return (
                  <div style={{ maxWidth: '16rem' }}>
                    <label className="text-sm font-medium text-gray-700 block mb-2">On</label>
                    {readOnly ? (
                      <TooltipProvider>
                        <div className="flex flex-wrap gap-2 min-h-[2.5rem] items-center w-full rounded-[8px] border border-solid px-4 py-2 text-base font-normal text-[#242424] md:text-sm border-[#e3e1e1] bg-[#f7f7f7]">
                          {selectedDays.length > 0 ? (
                            <>
                              {visibleDays.map(day => (
                                <span
                                  key={day}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-0.5 text-xs font-medium text-gray-500 gap-1 h-[20px] min-w-max"
                                >
                                  {dayLabels[day - 1]}
                                </span>
                              ))}
                              {remainingCount > 0 && (
                                <Tooltip delayDuration={300}>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center bg-gray-100 rounded-full px-2 py-0.5 text-xs font-medium text-gray-500 h-[20px] cursor-pointer hover:bg-gray-200">
                                      +{remainingCount}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="flex flex-col gap-1.5">
                                      {remainingDays.map(day => (
                                        <div
                                          key={day}
                                          className="flex items-center justify-between gap-2 text-sm"
                                        >
                                          <span className="text-white">{dayLabels[day - 1]}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">No days selected</span>
                          )}
                        </div>
                      </TooltipProvider>
                    ) : (
                      <SearchDropdownMultiSelect
                        items={[
                          { id: '1', label: 'Mon' },
                          { id: '2', label: 'Tue' },
                          { id: '3', label: 'Wed' },
                          { id: '4', label: 'Thu' },
                          { id: '5', label: 'Fri' },
                          { id: '6', label: 'Sat' },
                          { id: '7', label: 'Sun' },
                        ]}
                        selectedItems={selectedDays.map(d => d.toString())}
                        onSelectionChange={selectedIds => {
                          const days = selectedIds
                            .map(id => parseInt(id, 10))
                            .sort((a, b) => a - b);
                          onScheduleChange?.('days', days);
                        }}
                        placeholder="Search days"
                        triggerLabel={
                          selectedDaysCount > 0 ? `${selectedDaysCount} selected` : 'Select Days'
                        }
                        className="flex flex-row items-center w-full h-10 rounded-[8px] border border-solid px-4 py-0 text-base font-normal text-[#242424] placeholder:text-[#757271] focus:outline-none focus:ring-0 md:text-sm border-[#CCC8C6]"
                        disabled={readOnly}
                      />
                    )}
                  </div>
                );
              })()}
            {cadenceValue === 'monthly' &&
              (() => {
                // If cadence is manually set (not 'realtime'), use formData.scheduleConfig (user's selection)
                // Otherwise, use parsedSchedule from cron_info (initial load)
                const daySource =
                  formData.cadence && ['daily', 'weekly', 'monthly'].includes(formData.cadence)
                    ? formData.scheduleConfig?.day
                    : parsedSchedule?.day || formData.scheduleConfig?.day;
                const selectedDayItems = daySource
                  ? Array.isArray(daySource)
                    ? daySource
                    : [daySource]
                  : [1];
                const selectedDayCount = selectedDayItems.length;
                const sortedDayItems = [...selectedDayItems].sort((a, b) => a - b);
                const visibleDayItems = sortedDayItems.slice(0, 2);
                const remainingDayItems = sortedDayItems.slice(2);
                const remainingDayCount = remainingDayItems.length;
                return (
                  <div
                    style={
                      isTaxonomyTool
                        ? { width: '12rem', minWidth: '12rem', maxWidth: '12rem' }
                        : { maxWidth: '16rem' }
                    }
                  >
                    <label className="text-sm font-medium text-gray-700 block mb-2">Day</label>
                    {readOnly ? (
                      <TooltipProvider>
                        <div className="flex flex-wrap gap-2 min-h-[2.5rem] items-center w-full rounded-[8px] border border-solid px-4 py-2 text-base font-normal text-[#242424] md:text-sm border-[#e3e1e1] bg-[#f7f7f7]">
                          {selectedDayItems.length > 0 ? (
                            <>
                              {visibleDayItems.map(day => (
                                <span
                                  key={day}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-0.5 text-xs font-medium text-gray-500 gap-1 h-[20px] min-w-max"
                                >
                                  {getOrdinal(day)}
                                </span>
                              ))}
                              {remainingDayCount > 0 && (
                                <Tooltip delayDuration={300}>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center bg-gray-100 rounded-full px-2 py-0.5 text-xs font-medium text-gray-500 h-[20px] cursor-pointer hover:bg-gray-200">
                                      +{remainingDayCount}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="flex flex-col gap-1.5">
                                      {remainingDayItems.map(day => (
                                        <div
                                          key={day}
                                          className="flex items-center justify-between gap-2 text-sm"
                                        >
                                          <span className="text-white">{getOrdinal(day)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">No days selected</span>
                          )}
                        </div>
                      </TooltipProvider>
                    ) : (
                      <SearchDropdownMultiSelect
                        items={Array.from({ length: 31 }, (_, i) => ({
                          id: (i + 1).toString(),
                          label: getOrdinal(i + 1),
                        }))}
                        selectedItems={selectedDayItems.map(d => d.toString())}
                        onSelectionChange={selectedIds => {
                          const days = selectedIds
                            .map(id => parseInt(id, 10))
                            .sort((a, b) => a - b);
                          onScheduleChange?.('day', days);
                        }}
                        placeholder="Search days"
                        triggerLabel={
                          selectedDayCount > 0 ? `${selectedDayCount} selected` : 'Select Days'
                        }
                        className="flex flex-row items-center w-full h-10 rounded-[8px] border border-solid px-4 py-0 text-base font-normal text-[#242424] placeholder:text-[#757271] focus:outline-none focus:ring-0 md:text-sm border-[#CCC8C6]"
                        disabled={readOnly}
                      />
                    )}
                  </div>
                );
              })()}
            <div
              style={
                cadenceValue === 'hourly'
                  ? { width: '6rem', minWidth: '6rem' }
                  : isTaxonomyTool && cadenceValue === 'monthly'
                    ? { width: '14rem', minWidth: '14rem', maxWidth: '14rem' }
                    : { flex: '1 1 12rem', minWidth: '12rem', maxWidth: '15rem' }
              }
            >
              <label className="text-sm font-medium text-gray-700 block mb-2">
                {cadenceValue === 'hourly' ? 'Minute' : 'At'}
                {cadenceValue === '12_hourly' ? (
                  <span className="text-xs text-gray-500 font-normal block mt-0.5">
                    (9 AM & 9 PM)
                  </span>
                ) : null}
              </label>
              {cadenceValue === 'hourly' ? (
                <SingleSelect
                  key={`minute-${parsedSchedule?.time || formData.scheduleConfig?.time || '00:00'}`}
                  defaultValue={
                    parsedSchedule?.time || formData.scheduleConfig?.time
                      ? parseInt(
                          (parsedSchedule?.time || formData.scheduleConfig?.time || '00:00').split(
                            ':'
                          )[1] || '0',
                          10
                        ).toString()
                      : '0'
                  }
                  onChange={
                    readOnly
                      ? undefined
                      : (value: string) => {
                          const minute = parseInt(value || '0', 10);
                          const paddedMinute = minute.toString().padStart(2, '0');
                          onScheduleChange?.('time', `00:${paddedMinute}`);
                        }
                  }
                  disabled={readOnly}
                  options={Array.from({ length: 60 }, (_, i) => ({
                    label: i.toString().padStart(2, '0'),
                    value: i.toString(),
                  }))}
                  placeholder="Select minute"
                  fieldClassName="min-w-0"
                />
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SingleSelect
                      key={`hour-${parsedSchedule?.time || formData.scheduleConfig?.time || '09:00'}`}
                      defaultValue={
                        parsedSchedule?.time || formData.scheduleConfig?.time
                          ? parseInt(
                              (
                                parsedSchedule?.time ||
                                formData.scheduleConfig?.time ||
                                '09:00'
                              ).split(':')[0] || '9',
                              10
                            ).toString()
                          : '9'
                      }
                      onChange={
                        readOnly
                          ? undefined
                          : (hour: string) => {
                              const currentTime = formData.scheduleConfig?.time || '09:00';
                              const [, minute = '00'] = currentTime.split(':');
                              const paddedHour = hour.padStart(2, '0');
                              onScheduleChange?.('time', `${paddedHour}:${minute}`);
                            }
                      }
                      disabled={readOnly}
                      options={Array.from({ length: 24 }, (_, i) => ({
                        label: i.toString().padStart(2, '0'),
                        value: i.toString(),
                      }))}
                      placeholder="Hour"
                      fieldClassName="min-w-0"
                    />
                  </div>
                  <div className="flex-1">
                    <SingleSelect
                      key={`minute-${parsedSchedule?.time || formData.scheduleConfig?.time || '09:00'}`}
                      defaultValue={
                        parsedSchedule?.time || formData.scheduleConfig?.time
                          ? parseInt(
                              (
                                parsedSchedule?.time ||
                                formData.scheduleConfig?.time ||
                                '09:00'
                              ).split(':')[1] || '0',
                              10
                            ).toString()
                          : '0'
                      }
                      onChange={
                        readOnly
                          ? undefined
                          : (minute: string) => {
                              const currentTime = formData.scheduleConfig?.time || '09:00';
                              const [hour = '09'] = currentTime.split(':');
                              const paddedMinute = minute.padStart(2, '0');
                              onScheduleChange?.('time', `${hour}:${paddedMinute}`);
                            }
                      }
                      disabled={readOnly}
                      options={Array.from({ length: 60 }, (_, i) => ({
                        label: i.toString().padStart(2, '0'),
                        value: i.toString(),
                      }))}
                      placeholder="Minute"
                      fieldClassName="min-w-0"
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              style={
                isTaxonomyTool && cadenceValue === 'monthly'
                  ? { width: '16rem', minWidth: '16rem', maxWidth: '16rem' }
                  : { flex: '1 1 14rem', minWidth: '12rem', maxWidth: '16rem' }
              }
            >
              <SingleSelect
                label="Timezone"
                defaultValue={formData.scheduleConfig?.timezone || 'America/New_York'}
                onChange={readOnly ? undefined : value => onScheduleChange?.('timezone', value)}
                disabled={readOnly}
                options={[
                  { label: 'Eastern Standard Time', value: 'America/New_York' },
                  { label: 'Central Standard Time', value: 'America/Chicago' },
                  { label: 'Mountain Standard Time', value: 'America/Denver' },
                  { label: 'Pacific Standard Time', value: 'America/Los_Angeles' },
                  { label: 'Greenwich Mean Time', value: 'Europe/London' },
                  { label: 'Indian Standard Time', value: 'Asia/Kolkata' },
                  { label: 'Japan Standard Time', value: 'Asia/Tokyo' },
                ]}
                placeholder="Select timezone"
                fieldClassName="min-w-0 overflow-hidden"
              />
            </div>
          </div>
        </div>

        {/* Alerts Selected Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="text-base font-semibold text-gray-900">Alerts Selected</h4>
              <p className="text-sm text-gray-500 mt-1">
                {filteredThresholds.length} threshold{filteredThresholds.length !== 1 ? 's' : ''}{' '}
                configured
              </p>
            </div>
            <div className="flex items-center gap-3">
              <SearchDropdownMultiSelect
                items={uniqueBrands.map(brand => ({
                  id: brand,
                  label: brand,
                }))}
                selectedItems={selectedBrandFilters}
                onSelectionChange={setSelectedBrandFilters}
                placeholder="Search brands"
                triggerLabel={
                  selectedBrandFilters.length > 0
                    ? `${selectedBrandFilters.length} selected`
                    : 'Filter by Brand'
                }
                className="w-fit h-10"
              />
              {selectedBrandFilters.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 h-10"
                >
                  Clear Filters
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                <Input
                  placeholder="Search alerts..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-64 pl-10 h-10"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Brand
                  </th>
                  {isPacingTool ? (
                    <>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Dimension
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Metric
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Comparison Date Range
                      </th>
                    </>
                  ) : isTaxonomyTool ? (
                    <>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Taxonomy Type
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Value Type
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Pixel
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Event
                      </th>
                    </>
                  )}
                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Operator
                  </th>
                  {isPacingTool && (
                    <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                      Value Type
                    </th>
                  )}
                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Threshold
                  </th>
                  {!readOnly && (
                    <th className="px-5 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {paginatedThresholds.length > 0 ? (
                  paginatedThresholds.map((item, index) => {
                    // Find the brand to get all thresholds and check if this is the last threshold in the brand
                    const brand = selectedBrands.find(b => b.brandId === item.brandId);
                    const isLastInBrand =
                      brand && item.thresholdIndex === brand.thresholds.length - 1;
                    // Show logical operator row for all non-last thresholds (matching step 2 behavior)
                    const showLogicalOperatorRow = !isLastInBrand;

                    // Calculate ADD chain information (only within the same brand)
                    const prevItem = index > 0 ? paginatedThresholds[index - 1] : null;
                    const isPreviousRowAdd =
                      prevItem?.logicalOperator === 'ADD' && prevItem?.brandId === item.brandId;
                    const isCurrentRowAdd = item.logicalOperator === 'ADD';
                    let isInAddChain = false;
                    let addChainStart = index;
                    let addChainEnd = index;
                    let addChainLength = 1;

                    if (isPreviousRowAdd || isCurrentRowAdd) {
                      // Find the start of the ADD chain (within the same brand)
                      addChainStart = index;
                      while (
                        addChainStart > 0 &&
                        paginatedThresholds[addChainStart - 1].logicalOperator === 'ADD' &&
                        paginatedThresholds[addChainStart - 1].brandId === item.brandId
                      ) {
                        addChainStart--;
                      }

                      // Find the end of the ADD chain (within the same brand)
                      addChainEnd = index;
                      while (
                        addChainEnd < paginatedThresholds.length - 1 &&
                        paginatedThresholds[addChainEnd].logicalOperator === 'ADD' &&
                        paginatedThresholds[addChainEnd].brandId === item.brandId
                      ) {
                        addChainEnd++;
                      }

                      addChainLength = addChainEnd - addChainStart + 1;

                      // Only treat as ADD chain if there are at least 2 rows in the chain
                      // A single ADD row doesn't form a chain and should render normally
                      if (addChainLength > 1) {
                        isInAddChain = true;
                      }
                    }

                    // Calculate middle position
                    const middleIndex =
                      addChainLength % 2 === 0
                        ? addChainStart + addChainLength / 2 - 1
                        : addChainStart + Math.floor(addChainLength / 2);

                    const isMiddleRow = isInAddChain && index === middleIndex;

                    // Calculate rowSpan for even-numbered chains
                    const shouldSpanRows = isInAddChain && isMiddleRow && addChainLength % 2 === 0;
                    const rowSpan = shouldSpanRows ? addChainLength + 1 : 1;

                    // If in ADD chain but not middle row, skip operator and threshold cells
                    const shouldSkipOperatorAndThreshold = isInAddChain && !isMiddleRow;

                    // Check if current row connects to next via ADD (remove bottom border)
                    const hasAddToNext = item.logicalOperator === 'ADD';

                    // Border class helper - adds bottom border to separate rows, except for ADD connections
                    const getBorderClass = () => {
                      // Skip bottom border if not last row and connected to next via ADD
                      if (!isLastInBrand && hasAddToNext) {
                        // Connected to next row via ADD - no bottom border
                        return '';
                      }
                      // Add bottom border to separate from next row (or end of table if last row)
                      return 'border-b border-b-gray-200';
                    };

                    // For cells with rowspan (operator, threshold, actions), always add bottom border
                    const getRowSpanBorderClass = () => {
                      return 'border-b border-b-gray-200';
                    };

                    return (
                      <React.Fragment key={`${item.brandId}-${item.id || index}`}>
                        <tr className="summary-table-row">
                          <td
                            className={`px-5 py-4 text-sm font-medium text-gray-900 ${getBorderClass()}`}
                          >
                            {item.brandName}
                          </td>
                          {isPacingTool ? (
                            <>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {(() => {
                                  const dimensions = formatDimensionDisplay(
                                    item.dimension,
                                    item.dimensionValue
                                  );
                                  if (dimensions.length === 0) {
                                    return <span className="text-gray-400 italic">-</span>;
                                  }
                                  return (
                                    <div className="flex flex-col gap-1">
                                      {dimensions.map((dim, idx) =>
                                        dim.length > 40 ? (
                                          <TooltipProvider
                                            key={`${dim}-${idx}`}
                                            delayDuration={100}
                                          >
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="whitespace-nowrap cursor-default">
                                                  {`${dim.slice(0, 40)}...`}
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" align="start">
                                                {dim}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ) : (
                                          <span key={`${dim}-${idx}`} className="whitespace-nowrap">
                                            {dim}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {item.metric || <span className="text-gray-400 italic">-</span>}
                              </td>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {(() => {
                                  // Relative calendar type: show option label (e.g. "Last Month", "Last 7 Days")
                                  const thresholdItem = item as {
                                    calendarType?: string;
                                    comparisonDate?: string;
                                    startDate?: string;
                                    endDate?: string;
                                  };
                                  if (thresholdItem.calendarType === 'relative') {
                                    const optionId =
                                      thresholdItem.comparisonDate || thresholdItem.startDate;
                                    const label = optionId
                                      ? getRelativeDateRangeLabel(optionId)
                                      : null;
                                    if (label) return <span className="whitespace-nowrap">{label}</span>;
                                  }
                                  const dateRange = formatDateRange(item.endDate);
                                  if (dateRange) {
                                    return (
                                      <span className="whitespace-nowrap">
                                        {dateRange.start} - {dateRange.end}
                                      </span>
                                    );
                                  }
                                  return <span className="text-gray-400 italic">-</span>;
                                })()}
                              </td>
                            </>
                          ) : isTaxonomyTool ? (
                            <>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {item.taxonomyType ? (
                                  item.taxonomyType.charAt(0).toUpperCase() +
                                  item.taxonomyType.slice(1)
                                ) : (
                                  <span className="text-gray-400 italic">-</span>
                                )}
                              </td>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {item.taxonomyValueType ? (
                                  item.taxonomyValueType === 'updated_at' ? (
                                    'Updated At'
                                  ) : (
                                    item.taxonomyValueType.charAt(0).toUpperCase() +
                                    item.taxonomyValueType.slice(1)
                                  )
                                ) : (
                                  <span className="text-gray-400 italic">-</span>
                                )}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {item.pixelName || <span className="text-gray-400 italic">-</span>}
                              </td>
                              <td className={`px-5 py-4 text-sm text-gray-700 ${getBorderClass()}`}>
                                {item.eventName || <span className="text-gray-400 italic">-</span>}
                              </td>
                            </>
                          )}
                          {/* Operator column - show content in middle row, skip cells covered by rowspan */}
                          {!shouldSkipOperatorAndThreshold && (
                            <td
                              className={`px-5 py-4 ${rowSpan > 1 ? getRowSpanBorderClass() : getBorderClass()}`}
                              rowSpan={rowSpan}
                              style={{ verticalAlign: 'middle' }}
                            >
                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                {getDisplayOperator(item.operator)}
                              </span>
                            </td>
                          )}
                          {/* Pacing Value Type column - only for pacing tool */}
                          {isPacingTool && !shouldSkipOperatorAndThreshold && (
                            <td
                              className={`px-5 py-4 text-sm text-gray-700 ${rowSpan > 1 ? getRowSpanBorderClass() : getBorderClass()}`}
                              rowSpan={rowSpan}
                              style={{ verticalAlign: 'middle' }}
                            >
                              {(() => {
                                const thresholdItem = item as { pacingValueType?: string };
                                const valueType = thresholdItem.pacingValueType || 'percentage';
                                return valueType.charAt(0).toUpperCase() + valueType.slice(1);
                              })()}
                            </td>
                          )}
                          {/* Threshold column - show content in middle row, skip cells covered by rowspan */}
                          {!shouldSkipOperatorAndThreshold && (
                            <td
                              className={`px-5 py-4 text-sm font-semibold text-gray-900 ${rowSpan > 1 ? getRowSpanBorderClass() : getBorderClass()}`}
                              rowSpan={rowSpan}
                              style={{ verticalAlign: 'middle' }}
                            >
                              {isTaxonomyTool && item.taxonomyValueType === 'updated_at'
                                ? formatThresholdValue(item.threshold, item.taxonomyValueType)
                                : isPacingTool
                                  ? (() => {
                                      const pacingItem = item as { pacingValueType?: string };
                                      const showPct =
                                        !pacingItem.pacingValueType ||
                                        pacingItem.pacingValueType === 'percentage';
                                      return showPct ? `${item.threshold}%` : item.threshold;
                                    })()
                                  : item.threshold}
                            </td>
                          )}
                          {/* Actions column - show content in middle row, skip cells covered by rowspan */}
                          {!readOnly && !shouldSkipOperatorAndThreshold && (
                            <td
                              className={`px-5 py-4 text-center ${rowSpan > 1 ? getRowSpanBorderClass() : getBorderClass()}`}
                              rowSpan={rowSpan}
                              style={{ verticalAlign: 'middle' }}
                            >
                              <button
                                onClick={
                                  !onRemoveThresholdRow || allThresholds.length === 1
                                    ? undefined
                                    : () => {
                                        if (item.thresholdIndex !== undefined) {
                                          setPendingDeletion({
                                            brandId: item.brandId,
                                            thresholdIndex: item.thresholdIndex,
                                            brandName: item.brandName,
                                            pixelName: item.pixelName,
                                            eventName: item.eventName,
                                            dimension: item.dimension,
                                            metric: item.metric,
                                            startDate: item.startDate,
                                            endDate: item.endDate,
                                            calendarType: (item as { calendarType?: string }).calendarType,
                                            comparisonDate: (item as { comparisonDate?: string }).comparisonDate,
                                            taxonomyType: item.taxonomyType,
                                            taxonomyValueType: item.taxonomyValueType,
                                          });
                                        }
                                      }
                                }
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
                                  allThresholds.length === 1
                                    ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                    : 'text-red-600 hover:text-white hover:bg-red-600 bg-red-50 border border-red-200'
                                }`}
                                disabled={allThresholds.length === 1}
                                title={
                                  allThresholds.length === 1
                                    ? 'Cannot delete the last threshold'
                                    : 'Delete threshold'
                                }
                              >
                                <TrashAlt className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                        {/* Show logical operator row if not last in brand */}
                        {showLogicalOperatorRow && (
                          <tr>
                            <td
                              colSpan={isPacingTool ? (readOnly ? 7 : 8) : readOnly ? 5 : 6}
                              className={`border-x ${!hasAddToNext ? 'border-b border-b-gray-200' : ''} border-gray-300 px-4 py-3 bg-gray-50/30`}
                              style={{ borderTop: 'none' }}
                            >
                              <div
                                className="flex items-center relative pl-8"
                                style={{ height: '56px' }}
                              >
                                {/* Curly brace connector - only show if AND, OR, or ADD */}
                                {item.logicalOperator && item.logicalOperator !== 'NONE' && (
                                  <div
                                    className="absolute flex flex-col items-center"
                                    style={{ height: '100%', width: '24px', left: '16px' }}
                                  >
                                    {/* Top curve */}
                                    <div
                                      className={`w-full h-1/2 border-l-2 border-b-2 rounded-bl-lg ${
                                        item.logicalOperator === 'AND'
                                          ? 'border-green-500'
                                          : item.logicalOperator === 'OR'
                                            ? 'border-orange-500'
                                            : 'border-blue-500'
                                      }`}
                                      style={{ borderBottomLeftRadius: '10px' }}
                                    />
                                    {/* Bottom curve */}
                                    <div
                                      className={`w-full h-1/2 border-l-2 border-t-2 rounded-tl-lg ${
                                        item.logicalOperator === 'AND'
                                          ? 'border-green-500'
                                          : item.logicalOperator === 'OR'
                                            ? 'border-orange-500'
                                            : 'border-blue-500'
                                      }`}
                                      style={{ borderTopLeftRadius: '10px' }}
                                    />
                                  </div>
                                )}

                                {/* Logical Operator Display */}
                                <div className="flex items-center gap-3 ml-8">
                                  <span
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md shadow-sm ${
                                      item.logicalOperator === 'AND'
                                        ? 'bg-green-100 text-green-700'
                                        : item.logicalOperator === 'OR'
                                          ? 'bg-orange-100 text-orange-700'
                                          : item.logicalOperator === 'ADD'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {item.logicalOperator === 'AND'
                                      ? 'Both threshold conditions must meet'
                                      : item.logicalOperator === 'OR'
                                        ? 'Any threshold condition meets'
                                        : item.logicalOperator === 'ADD'
                                          ? 'Add threshold conditions'
                                          : 'No connection'}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-700">
                                    {item.logicalOperator || 'NONE'}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={isPacingTool ? (readOnly ? 6 : 7) : readOnly ? 5 : 6}
                      className="px-5 py-12 text-center"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <Search className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">
                          {hasActiveFilters
                            ? 'No thresholds match your search or filters'
                            : 'No thresholds configured'}
                        </p>
                        {hasActiveFilters && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearFilters}
                            className="mt-2"
                          >
                            Clear Filters
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredThresholds.length > 0 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Items per page</span>
                <select
                  value={pageSize}
                  onChange={
                    readOnly
                      ? undefined
                      : e => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }
                  }
                  disabled={readOnly}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={
                    readOnly ? undefined : () => setCurrentPage(prev => Math.max(1, prev - 1))
                  }
                  disabled={readOnly || currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                  aria-label="Previous page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
                  {filteredThresholds.length === 0
                    ? '0'
                    : `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, filteredThresholds.length)}`}{' '}
                  of {filteredThresholds.length}
                </span>
                <button
                  onClick={
                    readOnly
                      ? undefined
                      : () => setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={readOnly || currentPage >= totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                  aria-label="Next page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        <Dialog open={!!pendingDeletion} onOpenChange={open => !open && setPendingDeletion(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete threshold?</DialogTitle>
              <DialogDescription>
                <span className="block">Are you sure you want to delete this threshold?</span>
              </DialogDescription>
            </DialogHeader>
            {pendingDeletion && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-semibold">Brand:</span> {pendingDeletion.brandName}
                </p>
                {formData.toolName === 'Pacing Alert' ? (
                  <>
                    {pendingDeletion.dimension && (
                      <p>
                        <span className="font-semibold">Dimension:</span>{' '}
                        {pendingDeletion.dimension}
                      </p>
                    )}
                    {pendingDeletion.metric && (
                      <p>
                        <span className="font-semibold">Metric:</span> {pendingDeletion.metric}
                      </p>
                    )}
                    {pendingDeletion.calendarType === 'relative' &&
                      (pendingDeletion.comparisonDate || pendingDeletion.startDate) && (
                        <p>
                          <span className="font-semibold">Comparison Date Range:</span>{' '}
                          {getRelativeDateRangeLabel(
                            pendingDeletion.comparisonDate || pendingDeletion.startDate
                          ) || pendingDeletion.startDate}
                        </p>
                      )}
                    {pendingDeletion.startDate &&
                      pendingDeletion.endDate &&
                      pendingDeletion.calendarType !== 'relative' && (
                        <p>
                          <span className="font-semibold">Date Range:</span>{' '}
                          {pendingDeletion.startDate} to {pendingDeletion.endDate}
                        </p>
                      )}
                  </>
                ) : (
                  <>
                    {pendingDeletion.pixelName && (
                      <p>
                        <span className="font-semibold">Pixel:</span> {pendingDeletion.pixelName}
                      </p>
                    )}
                    {pendingDeletion.eventName && (
                      <p>
                        <span className="font-semibold">Event:</span> {pendingDeletion.eventName}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            <DialogFooter className="mt-4">
              <Button variant="secondary" onClick={() => setPendingDeletion(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (pendingDeletion && onRemoveThresholdRow) {
                    onRemoveThresholdRow(pendingDeletion.brandId, pendingDeletion.thresholdIndex);
                    setPendingDeletion(null);
                  }
                }}
              >
                Delete threshold
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Alert Body Section */}
        <div className="w-full min-w-0 overflow-hidden space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:gap-4">
            <div className="flex-1">
              <h4 className="text-base font-semibold text-gray-900">Alert Body</h4>
              <p className="text-sm text-gray-500 mt-1">
                {showPreview
                  ? 'Preview of your message templates with sample data'
                  : 'Preview and edit message templates for each channel'}
              </p>
            </div>
            <div className="flex items-center gap-3 justify-between">
              {/* Channel Tabs */}
              <div className="flex gap-0 bg-gray-100 p-1 rounded-lg w-fit">
                {channelsToShow.map(channel => (
                  <button
                    key={channel.key}
                    onClick={() => setSelectedChannel(channel.key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedChannel === channel.key
                        ? 'bg-white text-gray-800 shadow-sm'
                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {channel.key}
                  </button>
                ))}
              </div>
              {/* Preview Toggle Button */}
              <Button
                variant={showPreview ? 'default' : 'outline'}
                size="sm"
                onClick={handleTogglePreview}
                disabled={isLoadingPreview}
                className="flex items-center gap-2"
              >
                {isLoadingPreview ? (
                  <>
                    <SpinnerLoader size={14} />
                    <span>Loading...</span>
                  </>
                ) : showPreview ? (
                  <>
                    <EyeOff className="h-4 w-4" />
                    <span>Back to Editor</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    <span>Preview</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Show either Preview or Editor based on state */}
          {showPreview ? (
            <div
              className="border border-gray-200 rounded-lg min-w-0 overflow-hidden shadow-sm bg-gray-50 p-4"
              style={{ width: '60rem', maxWidth: '100%' }}
            >
              {previewContent.length > 0 ? (
                previewContent.map((brandPreview, index) => (
                  <div key={brandPreview.brand_id} className="space-y-4">
                    {/* Brand header - only show if multiple brands */}
                    {previewContent.length > 1 && (
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <span className="text-sm font-semibold text-gray-800">
                          {brandPreview.brand_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          (Brand ID: {brandPreview.brand_id})
                        </span>
                      </div>
                    )}

                    {/* Slack Preview */}
                    {selectedChannel === 'Slack' && brandPreview.slack && (
                      <div className="space-y-4">
                        <SlackMessageRenderer message={brandPreview.slack} />
                        {/* Slack Table Block */}
                        {brandPreview.slack_table_block && (
                          <div className="mt-4 overflow-x-auto">
                            <div className="text-sm font-semibold text-gray-700 mb-3">
                              Alert Details:
                            </div>
                            <table className="w-full border-collapse text-sm bg-white rounded">
                              <thead>
                                <tr className="bg-[#334238] text-white">
                                  {brandPreview.slack_table_block.rows[0]?.map(
                                    (header, colIndex) => {
                                      const colSettings =
                                        brandPreview.slack_table_block?.column_settings[colIndex];
                                      const align =
                                        colSettings?.align === 'right'
                                          ? 'right'
                                          : colSettings?.align === 'center'
                                            ? 'center'
                                            : 'left';
                                      return (
                                        <th
                                          key={colIndex}
                                          className="border border-gray-300 px-3 py-2 text-left font-semibold"
                                          style={{ textAlign: align }}
                                        >
                                          {header.text}
                                        </th>
                                      );
                                    }
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {brandPreview.slack_table_block.rows
                                  .slice(1)
                                  .map((row, rowIndex) => (
                                    <tr
                                      key={rowIndex}
                                      className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                                    >
                                      {row.map((cell, colIndex) => {
                                        const colSettings =
                                          brandPreview.slack_table_block?.column_settings[colIndex];
                                        const align =
                                          colSettings?.align === 'right'
                                            ? 'right'
                                            : colSettings?.align === 'center'
                                              ? 'center'
                                              : 'left';
                                        const whiteSpace = colSettings?.is_wrapped
                                          ? 'normal'
                                          : 'nowrap';
                                        return (
                                          <td
                                            key={colIndex}
                                            className="border border-gray-300 px-3 py-2"
                                            style={{ textAlign: align, whiteSpace }}
                                          >
                                            {cell.text}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedChannel === 'Slack' && !brandPreview.slack && (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No Slack preview available.
                      </div>
                    )}

                    {/* Email Preview */}
                    {selectedChannel === 'Email' && brandPreview.email && (
                      <div className="space-y-2">
                        {brandPreview.email_subject && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Subject:</span>{' '}
                            {brandPreview.email_subject}
                          </div>
                        )}
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{ __html: brandPreview.email }}
                        />
                      </div>
                    )}
                    {selectedChannel === 'Email' && !brandPreview.email && (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No Email preview available.
                      </div>
                    )}

                    {/* Web/Lift Notification Preview */}
                    {selectedChannel === 'Lift Notification' && brandPreview.web && (
                      <div
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: brandPreview.web }}
                      />
                    )}
                    {selectedChannel === 'Lift Notification' && !brandPreview.web && (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No Lift Notification preview available.
                      </div>
                    )}

                    {/* Separator between brands */}
                    {previewContent.length > 1 && index < previewContent.length - 1 && (
                      <hr className="border-gray-200 mt-4" />
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  No preview available. Please ensure you have message templates configured.
                </div>
              )}
            </div>
          ) : (
            /* Message Editor */
            <div className="border border-gray-200 rounded-lg w-full min-w-0 overflow-hidden shadow-sm">
              <ReactQuill
                ref={quillRef}
                className="editor-quill-mail text-sm"
                theme="snow"
                modules={modulesCustom}
                formats={formatsCustom}
                value={getCurrentMessageTemplate()}
                preserveWhitespace={true}
                onChange={readOnly ? undefined : handleEditorChange}
                readOnly={readOnly}
                style={{ maxWidth: '65rem' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
